import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { DaymadeDesignSystemPreviewComponentInfo } from '../';

interface ColourToken {
  name: string;
  value: string;
  token: string;
  swatch: string;
}

const brandColours: ColourToken[] = [
  {
    name: 'Primary',
    value: '#90F6DF',
    token: '--dm2025_daymadePrimary',
    swatch: 'var(--dm2025_daymadePrimary)',
  },
  {
    name: 'Grey 1',
    value: '#4A4A4A',
    token: '--dm2025_daymadeGrey1',
    swatch: 'var(--dm2025_daymadeGrey1)',
  },
  {
    name: 'Background 1',
    value: '#90F6DF - #C3FBF1',
    token: '--dm2025_daymadeBackground1Gradient',
    swatch: 'var(--dm2025_daymadeBackground1Gradient)',
  },
  {
    name: 'Grey 2',
    value: '#9B9B9B',
    token: '--dm2025_daymadeGrey2',
    swatch: 'var(--dm2025_daymadeGrey2)',
  },
  {
    name: 'Background 2',
    value: '#F6F6F6',
    token: '--dm2025_daymadeBackground2',
    swatch: 'var(--dm2025_daymadeBackground2)',
  },
  {
    name: 'White',
    value: '#FFFFFF',
    token: '--dm2025_daymadeWhite',
    swatch: 'var(--dm2025_daymadeWhite)',
  },
  {
    name: 'Outlines',
    value: '#E1E1E1',
    token: '--dm2025_daymadeOutline',
    swatch: 'var(--dm2025_daymadeOutline)',
  },
  {
    name: 'Disabled',
    value: '#C8C8C8',
    token: '--dm2025_daymadeDisabled',
    swatch: 'var(--dm2025_daymadeDisabled)',
  },
  {
    name: 'CTA',
    value: '#FF662F',
    token: '--dm2025_daymadeCta',
    swatch: 'var(--dm2025_daymadeCta)',
  },
];

const complementaryColours: ColourToken[] = [
  {
    name: 'Green assets',
    value: '#E8EED0 - #B8E986',
    token: '--dm2025_greenAssetsGradient',
    swatch: 'var(--dm2025_greenAssetsGradient)',
  },
  {
    name: 'Lite asset',
    value: '#FFDCBA - #FFB784',
    token: '--dm2025_liteAssetsGradient',
    swatch: 'var(--dm2025_liteAssetsGradient)',
  },
  {
    name: 'Winter asset',
    value: '#F4FFFF - #E6FFFF',
    token: '--dm2025_winterAssetsGradient',
    swatch: 'var(--dm2025_winterAssetsGradient)',
  },
  {
    name: 'Freemium asset',
    value: '#FDE3FC - #FFA8FB',
    token: '--dm2025_freemiumAssetsGradient',
    swatch: 'var(--dm2025_freemiumAssetsGradient)',
  },
  {
    name: 'Ads',
    value: '#C3FBF1',
    token: '--dm2025_adsColor',
    swatch: 'var(--dm2025_adsColor)',
  },
];

const ComponentPreview = () => {
  return (
    <AboutUsModule background="white">
      <AboutUsSiteContainer>
        <div className="designSystemColoursCard">
          <p className="designSystemColoursEyebrow">Colours</p>
          <div className="designSystemColoursDivider" />

          <section className="designSystemColoursSection">
            <h3 className="designSystemColoursHeading">Brand colours</h3>
            <div className="designSystemColoursGrid">
              {brandColours.map((colour) => (
                <ColourRow key={colour.name} colour={colour} />
              ))}
            </div>
          </section>

          <section className="designSystemColoursSection">
            <h3 className="designSystemColoursHeading">
              Complimentary colours
            </h3>
            <div className="designSystemAccentGrid">
              {complementaryColours.map((colour) => (
                <ColourRow key={colour.name} colour={colour} compact />
              ))}
            </div>
          </section>
        </div>

        <style jsx>{`
          .designSystemColoursCard {
            background: #a8bdf4;
            border-radius: 32px;
            padding: 42px 42px 48px;
            color: var(--dm2025_daymadeWhite);
          }

          .designSystemColoursEyebrow {
            margin: 0;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            font-size: 0.95rem;
          }

          .designSystemColoursDivider {
            height: 2px;
            background: var(--dm2025_daymadeWhite);
            opacity: 0.5;
            margin: 16px 0 28px;
          }

          .designSystemColoursSection + .designSystemColoursSection {
            margin-top: 48px;
          }

          .designSystemColoursHeading {
            margin: 0 0 28px;
            font-size: 1.7rem;
            line-height: 1.2;
            color: var(--dm2025_daymadeWhite);
          }

          .designSystemColoursGrid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 24px 40px;
          }

          .designSystemAccentGrid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 18px 40px;
          }

          @media (max-width: 800px) {
            .designSystemColoursCard {
              padding: 28px 24px 32px;
              border-radius: 24px;
            }

            .designSystemColoursGrid,
            .designSystemAccentGrid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
};

const ColourRow = ({
  colour,
  compact,
}: {
  colour: ColourToken;
  compact?: boolean;
}) => (
  <>
    <div className={`designSystemColourRow${compact ? ' compact' : ''}`}>
      <span
        className="designSystemColourSwatch"
        style={{ background: colour.swatch }}
      />
      <div>
        <p className="designSystemColourName">{colour.name}</p>
        <p className="designSystemColourToken">{colour.token}</p>
        <p className="designSystemColourValue">{colour.value}</p>
      </div>
    </div>

    <style jsx>{`
      .designSystemColourRow {
        display: flex;
        align-items: center;
        gap: 16px;
        min-height: 56px;
      }

      .designSystemColourRow.compact {
        min-height: 44px;
      }

      .designSystemColourSwatch {
        width: 42px;
        height: 42px;
        min-width: 42px;
        border-radius: 999px;
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
      }

      .designSystemColourName,
      .designSystemColourToken,
      .designSystemColourValue {
        margin: 0;
        color: var(--dm2025_daymadeWhite);
      }

      .designSystemColourName {
        font-size: 1rem;
        line-height: 1.2;
      }

      .designSystemColourToken {
        margin-top: 4px;
        font-size: 0.78rem;
        line-height: 1.2;
        opacity: 0.78;
      }

      .designSystemColourValue {
        margin-top: 4px;
        font-size: 0.95rem;
        line-height: 1.3;
        opacity: 0.95;
      }
    `}</style>
  </>
);

ComponentPreview.daymadeDesignSystemInfo = {
  name: 'Colours',
} as DaymadeDesignSystemPreviewComponentInfo;

export default ComponentPreview;
